import { Col, ConfigProvider, Row, theme } from 'antd';
import { ThemeConfig } from 'antd/es/config-provider/context';
import { StyleProvider, legacyLogicalPropertiesTransformer } from '@ant-design/cssinjs';
import './QMS.css';
import QMSTable from './QMSTable';
import useDefinedColumnsOverview from './useDefinedColumnsOverview';
import { CONFIG_SERVER } from '@vindr/services/constants/common.constant';
import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { MENU_TYPE, QMS_ITEM_STATUS } from './qms.constant';
import { useUpdateQueueItem, useUpdateQMSItemPosition, useGetQMSDetail } from '@vindr/services/actions/qms.action';
import { findOnlyOneDraggedItem } from '@vindr/utils/helpers';
import { ReactComponent as Logo } from '@vindr/ui/src/assets/logo-light.svg';
import { useGetDetailInstitution } from '@vindr/services/actions/institution.action';
import { QMS_STATUS } from '@vindr/services/apis/qms.api';
import { useTranslation } from 'react-i18next';
import { ErrorPage, Empty } from '@vindr/ui';

const LightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    fontSize: 12,
    borderRadiusSM: 2,
    borderRadiusLG: 2,
    borderRadius: 2,
    colorPrimary: '#4c80a4',
    colorBgContainer: '#fff',
    colorText: '#000',
    colorBgBase: '#f8fafb',
    controlOutlineWidth: 0,
    fontFamily: `Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`,
  },
  components: {
    Menu: {
      colorItemBgSelected: '#dfeffe',
    },
  },
};

let serverEvents = new Map<string, EventSource>();

const WrapQMSOverview = ({ isPopup = false, qmsId }: { isPopup?: boolean; qmsId: string }) => {
  const { t } = useTranslation('Vindoc');
  const { data: qms } = useGetQMSDetail(qmsId);
  return qms ? <QMSOverview isPopup={isPopup} qms={qms} /> : <ErrorPage
    title={t('Something went wrong')}
    description={t(
      'Please reload the page or try again after 24 hours. If you continue to have issues, contact the Hotline for assistance.'
    )}
  />;
}

const QMSOverview = ({ isPopup = false, qms }: { isPopup?: boolean; qms: any }) => {
  const { t } = useTranslation('Vindoc');
  const linkedQms = useMemo(() => (qms?.qms_linked || []).filter((it: any) => it.status === QMS_STATUS.Active), [qms]);
  const { mutate: updateQueueItem } = useUpdateQueueItem();
  const { mutate: updateQMSItemPosition } = useUpdateQMSItemPosition();
  const { data: institution } = useGetDetailInstitution(qms?.institution_id, { enabled: !!qms?.institution_id });

  const handleContextMenuSelect = useCallback(
    (type: MENU_TYPE, record) => {
      updateQueueItem({ id: record.id, data: { qms_status: QMS_ITEM_STATUS[type] } });
    },
    []
  );
  const overviewColumns = useDefinedColumnsOverview({ isPopup, handleContextMenuSelect, config: qms });
  const [queueData, setQueueData] = useState({});

  useEffect(() => {
    if (!linkedQms?.length) return;
    linkedQms.forEach((qms) => {
      if (!serverEvents.has(qms.id)) {
        serverEvents.set(qms.id, new EventSource(CONFIG_SERVER.BACKEND_URL.replace('/api', '') + '/qms/sse?qms_id=' + qms.id));
        serverEvents.get(qms.id).onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            setQueueData((prev: any) => ({ ...prev, [qms.id]: data }));
          } catch (error) {
            console.log(error);
          }
        };
      } else {
        serverEvents.get(qms.id).onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            setQueueData((prev: any) => ({ ...prev, [qms.id]: data }));
          } catch (error) {
            console.log(error);
          }
        };
      }
    });
    return () => {
      linkedQms.forEach((qms) => {
        if (serverEvents.has(qms.id)) {
          serverEvents.get(qms.id).close();
          serverEvents.delete(qms.id);
        }
      });
    };
  }, [linkedQms]);

  const handleDragSortEnd =  (
    qmsId: string,
    newDataSource: any[],
  ) => {
    console.log(newDataSource);
    const queue = queueData[qmsId];
    const waitingData = (queue || []).filter((it) => it.qms_status === QMS_ITEM_STATUS.WAITING);
    const newData = (newDataSource || []).filter((it) => it.qms_status === QMS_ITEM_STATUS.WAITING);

    const draggedItem = findOnlyOneDraggedItem(waitingData.map(it => it.id), newData.map(it => it.id));
    const tail = draggedItem.to === newData.length - 1 ? null : newData[draggedItem.to + 1].id;
    const head = draggedItem.to === 0 ? null : newData[draggedItem.to - 1].id;
    updateQMSItemPosition({ id: draggedItem.item, data: { tail, head } });
  };

  return (
    <div className="qms overview bg-white w-full">
      <ConfigProvider theme={LightTheme}>
        <StyleProvider transformers={[legacyLogicalPropertiesTransformer]}>
          <header className="flex h-20 items-center justify-center bg-secondary-lighter px-4 py-2">
            <section className="flex w-0 flex-auto items-center justify-center gap-4 overflow-hidden">
              <div className="flex justify-center gap-4">
                {institution?.data?.logo ? <img src={institution?.data?.logo} alt="" style={{ height: 56 }} /> : <Logo style={{ height: 56 }} />}
                <div className="flex items-center text-2xl text-gray-700 font-bold">
                  {qms?.institution?.name}
                </div>
              </div>
            </section>
          </header>
          <div className="p-3 w-full">
            <Row gutter={[16, 16]}>
              {!linkedQms?.length && <Empty className="w-full" description={t('No linked QMS')} />}
              {(linkedQms || []).map((qms) => (
                <Col xxl={linkedQms?.length === 1 ? 24 : 12} xl={24}>
                  <DeviceTable
                    columns={overviewColumns}
                    dataSource={queueData[qms.id]}
                    title={qms.name}
                    onDragSortEnd={(newDataSource) => handleDragSortEnd(qms.id, newDataSource)}
                  />
                </Col>
              ))}
            </Row>
          </div>
        </StyleProvider>
      </ConfigProvider>
    </div>
  );
};

const DeviceTable = ({
  dataSource,
  columns,
  title,
  onDragSortEnd,
}: {
  dataSource: any[];
  columns: any[];
  title: string;
  onDragSortEnd?: (newDataSource: any[]) => void;
}) => {
  const missCallData = (dataSource || []).filter((it) => it.qms_status === QMS_ITEM_STATUS.MISS_CALL);
  const waitingData = (dataSource || []).filter((it) => it.qms_status === QMS_ITEM_STATUS.WAITING);
  const inProgressData = (dataSource || []).filter((it) => it.qms_status === QMS_ITEM_STATUS.IN_PROGRESS);

  return (
    <QMSTable
      columns={columns}
      dataSource={[...inProgressData, ...waitingData, ...missCallData]}
      title={title}
      onDragSortEnd={onDragSortEnd}
    />
  );
}

export default memo(WrapQMSOverview);
